import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributionTable = ({ dealId, getAuthHeaders, mergedSyndications }) => {
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [distributeForm, setDistributeForm] = useState({
    payment_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: ''
  });
  const [lastDistribution, setLastDistribution] = useState(null);

  const handleDistributePayment = async () => {
    if (!dealId || !distributeForm.payment_amount) return;
    setDistributing(true);
    try {
      const payload = {
        payment_amount: parseFloat(distributeForm.payment_amount),
        payment_date: distributeForm.payment_date ? `${distributeForm.payment_date}T00:00:00Z` : undefined,
        payment_reference: distributeForm.payment_reference || undefined,
      };
      const res = await axios.post(`${API}/deals/${dealId}/distribute`, payload, { headers: getAuthHeaders() });
      setLastDistribution(res.data);
      toast.success(`Distributed ${res.data.distributions.length} syndicator shares`);
      setDistributeOpen(false);
      setDistributeForm({ payment_amount: '', payment_date: new Date().toISOString().split('T')[0], payment_reference: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Distribution failed');
    } finally {
      setDistributing(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />Payment Distribution Engine
          </CardTitle>
          <Button size="sm" onClick={() => setDistributeOpen(true)} className="bg-yellow-500 hover:bg-yellow-600 text-white" data-testid="btn-distribute-payment">
            <Zap className="h-4 w-4 mr-1" />Distribute Payment
          </Button>
        </CardHeader>
        {lastDistribution && (
          <CardContent>
            <p className="text-xs text-gray-500 mb-2">Last distribution: {lastDistribution.payment_amount ? formatCurrency(lastDistribution.payment_amount) : ''} on {lastDistribution.payment_date?.split('T')[0]}</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Syndicator</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Gross Share</TableHead>
                    <TableHead className="text-right">Coll. Fee</TableHead>
                    <TableHead className="text-right">Broker Comm.</TableHead>
                    <TableHead className="text-right">Orig. Credit</TableHead>
                    <TableHead className="text-right font-bold">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lastDistribution.distributions?.map((d, i) => (
                    <TableRow key={d.syndicator_id || i}>
                      <TableCell className="font-medium text-sm">{d.syndicator_name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{d.role}</Badge></TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(d.gross_share)}</TableCell>
                      <TableCell className="text-right text-sm text-red-600">
                        {d.role !== 'INVESTOR' ? formatCurrency(d.collection_fee_charged) : <span className="text-gray-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm text-red-600">
                        {d.role !== 'PARTNER' ? formatCurrency(d.broker_commission_charged) : <span className="text-gray-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm text-green-600">
                        {(d.role === 'PARTNER' || d.role === 'INVESTOR_FEE') ? formatCurrency(d.origination_fee_credit) : <span className="text-gray-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(d.net_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        )}
      </Card>

      <Dialog open={distributeOpen} onOpenChange={setDistributeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Distribute Payment</DialogTitle>
            <DialogDescription>Enter payment details to calculate and record syndicator distributions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Payment Amount *</Label>
              <Input type="number" step="0.01" min="0" placeholder="e.g. 5000" value={distributeForm.payment_amount} onChange={e => setDistributeForm(f => ({ ...f, payment_amount: e.target.value }))} data-testid="distribute-amount-input" />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input type="date" value={distributeForm.payment_date} onChange={e => setDistributeForm(f => ({ ...f, payment_date: e.target.value }))} data-testid="distribute-date-input" />
            </div>
            <div>
              <Label>Reference / Transaction ID (optional)</Label>
              <Input placeholder="e.g. TXN-1234" value={distributeForm.payment_reference} onChange={e => setDistributeForm(f => ({ ...f, payment_reference: e.target.value }))} data-testid="distribute-ref-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistributeOpen(false)}>Cancel</Button>
            <Button onClick={handleDistributePayment} disabled={distributing || !distributeForm.payment_amount} className="bg-yellow-500 hover:bg-yellow-600 text-white" data-testid="btn-confirm-distribute">
              {distributing ? 'Distributing...' : 'Distribute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DistributionTable;
